import * as React from 'react';
import { Form, AlertMessage, AlertType, SpinnerWrapper, HiddenInput } from "soprasteria-ui-library";
import { IOrthodonticLkgCfPatientExtension, IOrthodonticLkgCfPatientExtensionCreate } from '../../../models/IOrthodonticLkgCfPatientExtension';
import { OrthodonticLkgCfPatientExtensionAdministrationApi } from '../../../api/OrthodonticLkgCfPatientExtensionAdministrationApi';
import { OrthodonticLkgCfPatientExtensionApi } from '../../../api/OrthodonticLkgCfPatientExtensionApi';
import { useAuth } from '../../../App';
import { setMessage } from '../../../helpers/MessageHelper';
import { OrthodonticLkgCfPatientExtensionFooterButtons } from './OrthodonticLkgCfPatientExtensionFooterButtons';
import { OrthodonticLkgCfPatientExtensionInputs } from './OrthodonticLkgCfPatientExtensionSections/OrthodonticLkgCfPatientExtensionInputs';
import { IPatient } from '../../../models/IPatient';
import { OrthodonticLkgCfPatientExtensionPatientInformation } from './OrthodonticLkgCfPatientExtensionSections/OrthodonticLkgCfPatientExtensionPatientInformation';
import { OrthodonticLkgCfPatientExtensionCareProviderInformation } from './OrthodonticLkgCfPatientExtensionSections/OrthodonticLkgCfPatientExtensionCareProviderInformation';
import { useHistory, useParams } from 'react-router';
import { ORTHODONTIC_LKGCF_PATIENT_EXTENSION } from '../../../utils/RouteConstants';
import { OrthodonticLkgCfPatientExtensionApproveModal } from './OrthodonticLkgCfPatientExtensionModals/OrthodonticLkgCfPatientExtensionApproveModal';
import { OrthodonticLkgCfPatientExtensionRejectModal } from './OrthodonticLkgCfPatientExtensionModals/OrthodonticLkgCfPatientExtensionRejectModal';

interface IOrthodonticLkgCfPatientExtensionView {
    patient: IPatient;
}

export const OrthodonticLkgCfPatientExtension: React.FunctionComponent<IOrthodonticLkgCfPatientExtensionView> = ({ patient }) => {

    const auth = useAuth();
    const { id } = useParams<{id: string}>();
    const orthodonticLkgCfPatientExtensionApi = new OrthodonticLkgCfPatientExtensionApi(auth);
    const orthodonticLkgCfPatientExtensionAdministrationApi = new OrthodonticLkgCfPatientExtensionAdministrationApi(auth);
    const [orthodonticLkgCfPatientExtension, setOrthodonticLkgCfPatientExtension] = React.useState<IOrthodonticLkgCfPatientExtension>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isSendingOrthodonticLkgCfPatientExtensionRequest, setIsSendingOrthodonticLkgCfPatientExtensionRequest] = React.useState<boolean>(false);
    const [isFetchingOrthodonticLkgCfPatientExtension, setIsFetchingOrthodonticLkgCfPatientExtension] = React.useState<boolean>(false);
    const [isDecisionReasonRequired, setIsDecisionReasonRequired] = React.useState<boolean>(false);
    const history = useHistory();

    React.useEffect(() => {
        getOrthodonticLkgCfPatientExtension(parseInt(id));
    }, [])

    const getOrthodonticLkgCfPatientExtension = (orthodonticLkgCfPatientExtensionId: number) => {
        setIsFetchingOrthodonticLkgCfPatientExtension(true);
        orthodonticLkgCfPatientExtensionApi.getOrthodonticLkgCfPatientExtension(orthodonticLkgCfPatientExtensionId,
            (data) => setOrthodonticLkgCfPatientExtension(data),
            () => setIsFetchingOrthodonticLkgCfPatientExtension(false)
        );
    }

    const sendOrthodonticLkgCfPatientExtensionRequest = (data: IOrthodonticLkgCfPatientExtensionCreate) => {
        setIsSendingOrthodonticLkgCfPatientExtensionRequest(true);
        orthodonticLkgCfPatientExtensionAdministrationApi.createOrthodonticLkgCfPatientExtension(data, (orthodonticLkgCfPatientExtensionId) => {
            history.replace(`${ORTHODONTIC_LKGCF_PATIENT_EXTENSION}/${orthodonticLkgCfPatientExtensionId}`);
            const newMessage = setMessage("Ansökan om förlänging har skickats", successMessage ?? "");
            setSuccessMessage(newMessage);
            getOrthodonticLkgCfPatientExtension(orthodonticLkgCfPatientExtensionId);
        }, (error) => {
            const newMessage = setMessage(error, errorMessage ?? "");
            setErrorMessage(newMessage);
        }, () => setIsSendingOrthodonticLkgCfPatientExtensionRequest(false)
        );
    }



    return (
        <SpinnerWrapper isLoading={isFetchingOrthodonticLkgCfPatientExtension}>
            <Form>
                <HiddenInput name="patientId" value={patient.patientId.toString()} />
                <HiddenInput name="orthodonticLkgCfPatientExtensionId" value={orthodonticLkgCfPatientExtension?.orthodonticLkgCfPatientExtensionId.toString() ?? "0"} />
                <section className="card">
                    <div className="card-header">
                        <div className="row">
                            <div className="col">
                                <h1>Ansökan om förlängning av LKG/CF {orthodonticLkgCfPatientExtension?.orthodonticLkgCfPatientExtensionNumber ? "- " + orthodonticLkgCfPatientExtension.orthodonticLkgCfPatientExtensionNumber : ""}</h1>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                        <OrthodonticLkgCfPatientExtensionPatientInformation patient={patient} />
                        <hr />
                        {
                            orthodonticLkgCfPatientExtension ?
                                <>
                                    <OrthodonticLkgCfPatientExtensionCareProviderInformation careProvider={orthodonticLkgCfPatientExtension.careProvider} />
                                    <hr />
                                </>
                                : null
                        }
                        <OrthodonticLkgCfPatientExtensionInputs orthodonticLkgCfPatientExtension={orthodonticLkgCfPatientExtension} patientId={patient.patientId} />
                        {
                            orthodonticLkgCfPatientExtension?.canMakeDecision ?
                                <>
                                    <OrthodonticLkgCfPatientExtensionApproveModal setSuccessMessage={setSuccessMessage} getOrthodonticLkgCfPatientExtension={getOrthodonticLkgCfPatientExtension} orthodonticLkgCfPatientExtensionId={orthodonticLkgCfPatientExtension.orthodonticLkgCfPatientExtensionId} />
                                    <OrthodonticLkgCfPatientExtensionRejectModal setSuccessMessage={setSuccessMessage} getOrthodonticLkgCfPatientExtension={getOrthodonticLkgCfPatientExtension} orthodonticLkgCfPatientExtensionId={orthodonticLkgCfPatientExtension.orthodonticLkgCfPatientExtensionId} isDecisionReasonRequired={isDecisionReasonRequired} />
                                </>
                                : null
                        }
                    </div>
                    <div className="card-footer">
                        <OrthodonticLkgCfPatientExtensionFooterButtons canCreateOrthodonticLkgCfPatientExtension={orthodonticLkgCfPatientExtension === undefined} isSendingOrthodonticLkgCfPatientExtensionRequest={isSendingOrthodonticLkgCfPatientExtensionRequest} sendOrthodonticLkgCfPatientExtensionRequest={sendOrthodonticLkgCfPatientExtensionRequest} canMakeDecision={orthodonticLkgCfPatientExtension?.canMakeDecision ?? false} setIsDecisionReasonRequired={setIsDecisionReasonRequired}/>
                    </div>
                </section>
            </Form>
        </SpinnerWrapper >
    )
}